import { ThemeOptions, PaletteMode } from '@mui/material';
import { lightTokens, darkTokens, commonTokens, paletteExtensions } from './tokens';
import { spacingTokens, iconSizes } from './default';
import { inspire, inspireMed, inspireBlack, inspireHeavy } from './fonts';

// Switch Icons (theme-aware)
const toSvgXml = (content: string) => `url('data:image/svg+xml;utf8,${content}')`;

const getCancelSvg = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 18 18"><path fill="${encodeURIComponent(
    '#1D2026',
  )}" d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"/></svg>`;

const getCheckSvg = (mode: PaletteMode) =>
  `<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 18 18"><path fill="${encodeURIComponent(
    mode === 'light' ? '#fff' : '#1D2026',
  )}" d="M6.75109 12.1284L3.62359 9.00086L2.55859 10.0584L6.75109 14.2509L15.7511 5.25086L14.6936 4.19336L6.75109 12.1284Z"/></svg>`;

export const getThemeConfig = (mode: PaletteMode): ThemeOptions => {
  const themeTokens = mode === 'light' ? lightTokens : darkTokens;
  const extensions = mode === 'light' ? paletteExtensions.light : paletteExtensions.dark;
  const componentColors = mode === 'light' ? commonTokens.component : commonTokens.componentDark;
  const changeRequestedTokens = mode === 'light' ? commonTokens.changeRequested : commonTokens.changeRequestedDark;

  return {
    palette: {
      mode,
      ...themeTokens,
      gray: commonTokens.gray,
      changeRequested: changeRequestedTokens,
      error: { ...themeTokens.error, ...extensions.error },
      warning: { ...themeTokens.warning, ...extensions.warning },
      info: { ...themeTokens.info, ...extensions.info },
      success: { ...themeTokens.success, ...extensions.success },
    },
    spacingTokens,
    typography: {
      fontFamily: 'Inspire, Helvetica Neue, Arial, sans-serif',
      h2: {
        fontSize: '3.75rem',
        fontWeight: 800,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: '3rem',
        fontStyle: 'normal',
        fontWeight: 800,
        lineHeight: 1.2,
        textAlign: 'left',
      },
      h4: {
        fontSize: '2.125rem',
        fontStyle: 'normal',
        fontWeight: 800,
        lineHeight: 1.2,
        textAlign: 'left',
      },
      h5: {
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 800,
        lineHeight: 'normal',
        letterSpacing: '0.18px',
        textAlign: 'left',
      },
      h6: {
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '1.2',
        letterSpacing: '0.15px',
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.429,
        letterSpacing: 0,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 800,
        lineHeight: '1.5',
        letterSpacing: '0.15px',
        textTransform: 'none',
      },
      subtitle2: {
        fontFamily: '"Inspire", "Helvetica Neue", Arial, sans-serif',
        fontSize: '0.875rem',
        lineHeight: '20px',
        fontWeight: 600,
      },
      overline: {
        fontSize: '0.625rem',
        letterSpacing: '1.6px',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: '1.2',
        letterSpacing: '0.4px',
      },
      button: {
        fontSize: '.875rem',
        textTransform: 'uppercase',
      },
    },
    breakpoints: {
      values: {
        xs: 320,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          ${inspire}
          ${inspireMed}
          ${inspireBlack}
          ${inspireHeavy}

          a {
            color: inherit;
            text-decoration: underline;
          }

          body *::-webkit-scrollbar {
            -webkit-appearance: none;
          }

          body *::-webkit-scrollbar:vertical {
            width: 12px;
          }

          body *::-webkit-scrollbar:horizontal {
            height: 12px;
          }

          body *::-webkit-scrollbar-thumb {
            border-radius: 8px;
            border: 2px solid ${mode === 'light' ? 'white' : '#1D2026'};
            background-color: ${mode === 'light' ? '#A9A9A9' : '#666666'};
          }

          .mantine-RichTextEditor-root {
            border-color: ${mode === 'light' ? 'rgba(29, 32, 38, 0.23)' : 'rgba(247, 247, 249, 0.18)'} !important;
          }

          .partner-portal-tiptap-toolbar,
          .mantine-RichTextEditor-toolbar {
            background-color: transparent !important;
            border-bottom: none !important;
            color: ${mode === 'light' ? 'inherit' : '#F7F7F9'} !important;
          }

          .partner-portal-tiptap-editor .partner-portal-tiptap-content,
          .partner-portal-tiptap-content,
          .mantine-RichTextEditor-content {
            background-color: ${mode === 'light' ? '#FFFFFF' : '#282C34'} !important;
          }

          .partner-portal-tiptap-content .ProseMirror,
          .mantine-RichTextEditor-content .ProseMirror {
            color: ${mode === 'light' ? 'inherit' : '#F7F7F9'};
          }

          .partner-portal-tiptap-content .ProseMirror p,
          .mantine-RichTextEditor-content .ProseMirror p {
            margin: 0;
          }

          .partner-portal-tiptap-content .ProseMirror ul,
          .partner-portal-tiptap-content .ProseMirror ol,
          .mantine-RichTextEditor-content .ProseMirror ul,
          .mantine-RichTextEditor-content .ProseMirror ol {
            margin: 0;
            padding-left: 20px;
          }

          .partner-portal-tiptap-content .ProseMirror p.is-editor-empty:first-of-type::before,
          .mantine-RichTextEditor-content .ProseMirror p.is-editor-empty:first-of-type::before {
            color: ${mode === 'light' ? 'rgba(29, 32, 38, 0.6)' : 'rgba(247, 247, 249, 0.6)'} !important;
            opacity: 1 !important;
          }

          .rich-text-content :not(pre) > code,
          .rich-text-inline-code,
          .ProseMirror :not(pre) > code,
          .ProseMirror .rich-text-inline-code,
          .mantine-RichTextEditor-content :not(pre) > code,
          .mantine-RichTextEditor-content .rich-text-inline-code {
            background-color: ${mode === 'light' ? 'rgba(29, 32, 38, 0.08)' : 'rgba(247, 247, 249, 0.12)'} !important;
            color: ${mode === 'light' ? 'inherit' : '#F7F7F9'} !important;
            border: none !important;
            padding: 0.1em 0.35em;
            border-radius: 4px;
            font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
            font-size: 0.875em;
          }

          pre.rich-text-code-block,
          .rich-text-content pre,
          .ProseMirror pre,
          .mantine-RichTextEditor-content pre {
            background-color: ${mode === 'light' ? '#f2f2f2' : '#14161A'} !important;
            color: ${mode === 'light' ? 'inherit' : '#F7F7F9'} !important;
            border-radius: 4px;
            border: 1px solid ${mode === 'light' ? '#bbb' : 'rgba(247, 247, 249, 0.18)'};
            padding: 0.75rem;
            font-size: 0.75rem;
            overflow: auto;
            margin: 0.5rem 0;
          }

          pre.rich-text-code-block code,
          .rich-text-content pre code,
          .ProseMirror pre code,
          .mantine-RichTextEditor-content pre code {
            white-space: break-spaces;
            font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
            background: transparent !important;
            color: inherit !important;
            padding: 0;
            border-radius: 0;
            font-size: inherit;
          }
      `,
      },
      MuiSvgIcon: {
        variants: [
          {
            props: { fontSize: 'xs' },
            style: {
              fontSize: iconSizes.xs,
            },
          },
        ],
      },
      MuiList: {
        styleOverrides: {
          root: {
            '&.two-col-select': {
              width: '368px',
            },
            '&.two-col-select .column': {
              width: '43%',
              float: 'left',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingRight: '16px',
            paddingLeft: '16px',
            margin: '0 16px 1px',
            borderBottom: 'none',
            '&.Mui-focused': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
            '&.Mui-focusVisible': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
            '&.last': {
              borderBottom: '0',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            whiteSpace: 'initial',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: themeTokens.action.selected,
              '&:hover': {
                backgroundColor: themeTokens.action.selected,
              },
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? 'rgba(0,0,0,0.87)' : 'rgba(255,255,255,0.87)',
            '&.Mui-focused': {
              color: mode === 'light' ? 'rgba(0,0,0,0.87)' : 'rgba(255,255,255,0.87)',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: '#6C696A',
          },
          thumb: {
            backgroundImage: toSvgXml(getCancelSvg()),
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          },
          switchBase: {
            '&.Mui-checked ': {
              '& + .MuiSwitch-track': {
                backgroundColor: mode === 'light' ? 'rgba(10, 92, 51, 0.5)' : 'rgba(163, 245, 204, 0.5)',
              },
              '& .MuiSwitch-thumb': {
                backgroundImage: toSvgXml(getCheckSvg(mode)),
                color: mode === 'light' ? themeTokens.success.main : darkTokens.success.main,
              },
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              backgroundImage: 'none',
              color: 'unset',
            },
            '&.Mui-disabled': {
              color: '#B5B4B4',
              '&.Mui-checked': {
                color: '#B5B4B4',
              },
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: mode === 'light' ? 'rgba(35, 31, 32, 0.26)' : 'rgba(255, 255, 255, 0.26)',
              },
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? 'rgba(0,0,0,0.87)' : 'rgba(255,255,255,0.87)',
            '&.Mui-checked': {
              color: themeTokens.primary.main,
            },
            '&.MuiRadio-colorSecondary.Mui-checked': {
              color: themeTokens.primary.main,
            },
            '&:hover': {
              background: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 400,
          },
          label: {
            paddingLeft: '12px',
            paddingRight: '12px',
          },
          labelSmall: {
            paddingLeft: '8px',
            paddingRight: '8px',
          },
          colorPrimary: {
            color: themeTokens.primary.main,
            border: `1px solid ${themeTokens.primary.main}`,
            '&.MuiChip-filledPrimary': {
              backgroundColor: themeTokens.action.active,
              color: mode === 'light' ? 'white' : themeTokens.primary.contrastText,
              border: `1px solid ${themeTokens.action.active}`,
            },
          },
          iconSmall: {
            marginRight: '8px',
            marginLeft: 0,
          },
          colorInfo: {
            color: themeTokens.info.main,
            border: `1px solid ${themeTokens.info.main}`,
            '&.MuiChip-filledInfo': {
              backgroundColor: themeTokens.info.main,
              color: themeTokens.info.contrastText,
              border: `1px solid ${themeTokens.info.main}`,
            },
          },
          colorSuccess: {
            color: themeTokens.success.main,
            border: `1px solid ${themeTokens.success.main}`,
            '&.MuiChip-filledSuccess': {
              backgroundColor: themeTokens.success.main,
              color: themeTokens.success.contrastText,
              border: `1px solid ${themeTokens.success.main}`,
            },
          },
          colorWarning: {
            color: themeTokens.warning.main,
            border: `1px solid ${themeTokens.warning.main}`,
            '&.MuiChip-filledWarning': {
              backgroundColor: themeTokens.warning.main,
              color: themeTokens.warning.contrastText,
              border: `1px solid ${themeTokens.warning.main}`,
            },
          },
          colorError: {
            color: themeTokens.error.main,
            border: `1px solid ${themeTokens.error.main}`,
            '&.MuiChip-filledError': {
              backgroundColor: themeTokens.error.main,
              color: themeTokens.error.contrastText,
              border: `1px solid ${themeTokens.error.main}`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: mode === 'light' ? '1px solid #DBDBDD' : '1px solid rgba(247,247,249,0.18)',
            marginBottom: '1.5rem',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '1.5rem 0',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            '&[aria-selected="true"]': {
              backgroundColor: 'transparent',
            },
            '&:hover': {
              backgroundColor: mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.08)',
            },
            '&:active': {
              backgroundColor: mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.08)',
            },
          },
          listbox: {
            '&::-webkit-scrollbar': {
              '-webkit-appearance': 'none',
              '&:vertical': {
                width: '12px',
              },
              '&:horizontal': {
                height: '12px',
              },
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '8px',
              border: `2px solid ${mode === 'light' ? 'white' : '#1D2026'}`,
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.25)',
            },
          },
          clearIndicator: {
            '&:hover': {
              cursor: 'pointer',
            },
          },
        },
      },
      MuiGridLegacy: {
        styleOverrides: {
          'spacing-xs-2': {
            width: 'calc(100% + 16px)',
            margin: '-8px',
            '> .MuiGridLegacy-item': {
              padding: '8px',
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: mode === 'light' ? 'rgba(35, 31, 32, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              opacity: 1,
            },
          },
          root: {
            '& textarea::placeholder': {
              color: mode === 'light' ? 'rgba(35, 31, 32, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              opacity: 1,
            },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            '&.MuiFormLabel': {
              color: mode === 'light' ? 'rgba(0,0,0,0.87)' : 'rgba(255,255,255,0.87)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            lineHeight: '1em',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: themeTokens.info.main,
          },
        },
      },
      MuiTimelineItem: {
        styleOverrides: {
          root: {
            ':before': {
              flex: '0',
              padding: '0',
            },
          },
        },
      },
      MuiTabPanel: {
        styleOverrides: {
          root: {
            padding: '32px 0 0',
            '& .MuiContainer-root': {
              paddingTop: 0,
              marginTop: 0,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            padding: 0,
            minHeight: 0,
            '& .MuiTabs-indicator': {
              height: '3px',
              backgroundColor: themeTokens.primary.main,
            },
          },
        },
      },
      MuiTab: {
        defaultProps: {
          TouchRippleProps: { center: true },
        },
        styleOverrides: {
          root: {
            fontSize: '1rem',
            padding: '9px 16px',
            minWidth: 0,
            minHeight: 0,
            fontWeight: 700,
            textTransform: 'none',
            lineHeight: 1,
            margin: '0 12px 0 0',
            '&:hover': {
              color: mode === 'light' ? 'rgba(35, 31, 32, 1)' : 'rgba(247, 247, 249, 1)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(29, 32, 38, 0.9)',
            borderRadius: '4px',
            color: '#FFFFFF',
            fontSize: '.75rem',
            maxWidth: '300px',
          },
          popper: {
            '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip.MuiTooltip-tooltipPlacementBottom': {
              marginTop: '3px',
            },
            '&[data-popper-placement*="top"] .MuiTooltip-tooltip.MuiTooltip-tooltipPlacementTop': {
              marginBottom: '3px',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            letterSpacing: '1.25px',
            textTransform: 'capitalize',
            fontSize: '0.875rem',
          },
          sizeSmall: {
            paddingLeft: '8px',
            paddingRight: '8px',
          },
          outlinedPrimary: {
            borderColor: themeTokens.primary.main,
          },
          outlinedError: {
            borderColor: themeTokens.error.main,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: mode === 'light' ? '#6C696A' : 'rgba(255,255,255,0.16)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '&.changesRequested .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: changeRequestedTokens.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: changeRequestedTokens.main,
              },
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.Mui-error': {
              color: themeTokens.error.main,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: themeTokens.info.main,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: themeTokens.error.main,
            },
            '&:hover .button': {
              borderColor: themeTokens.primary.main,
            },
          },
          notchedOutline: {
            '&.changesRequested .MuiOutlinedInput-root': {
              borderColor: changeRequestedTokens.main,
            },
            '&.changesRequested.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: changeRequestedTokens.main,
            },
          },
        },
      },
      MuiTimelineDot: {
        styleOverrides: {
          root: {
            margin: '8px 0',
          },
        },
      },
      MuiTimelineConnector: {
        styleOverrides: {
          root: {
            width: '1px',
            backgroundColor: mode === 'light' ? 'rgba(35, 31, 32, 0.12)' : 'rgba(247, 247, 249, 0.18)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: componentColors.error.background,
            color: componentColors.error.content,
          },
          standardWarning: {
            backgroundColor: componentColors.warning.background,
            color: componentColors.warning.content,
          },
          standardInfo: {
            backgroundColor: componentColors.info.background,
            color: componentColors.info.content,
          },
          standardSuccess: {
            backgroundColor: componentColors.success.background,
            color: componentColors.success.content,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 78,
          disableRowSelectionOnClick: true,
          disableColumnFilter: true,
          isCellEditable: () => false,
        },
        styleOverrides: {
          // Transparent root so the margin gap between toolbar (filters) and the
          // table shows the page background in dark mode instead of DataGrid paper.
          root: { border: '0', backgroundColor: 'transparent' },
          main: {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: mode === 'light' ? '#B7B7BC' : 'rgba(247,247,249,0.18)',
            borderRadius: '8px 8px 0 0',
            marginTop: '32px',
            // Let the page background show through the grid area instead of the
            // default paper fill (invisible in light mode, a lighter block in dark).
            backgroundColor: 'transparent',
          },
          footerContainer: {
            borderWidth: '0 1px 1px',
            borderStyle: 'solid',
            borderColor: mode === 'light' ? '#B7B7BC' : 'rgba(247,247,249,0.18)',
            borderRadius: '0 0 8px 8px',
            '&.MuiDataGrid-withBorderColor': {
              borderColor: mode === 'light' ? '#B7B7BC' : 'rgba(247,247,249,0.18)',
            },
          },
          toolbar: { border: '0', padding: '0' },
          row: {
            borderColor: mode === 'light' ? 'rgba(35, 31, 32, 0.12)' : 'rgba(247, 247, 249, 0.18)',
            '&:hover': {
              background: 'transparent',
            },
          },
          columnHeader: {
            border: 'none',
            padding: '0 16px',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          columnHeaderTitle: {
            fontSize: '0.875rem',
            lineHeight: '20px',
            fontWeight: 700,
          },
          columnSeparator: {
            display: 'none',
          },
          cell: {
            padding: '0 16px',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          menu: {
            color: themeTokens.text.primary,
            padding: 0,
            '& .MuiSvgIcon-root': {
              color: themeTokens.text.primary,
            },
            '& li.MuiButtonBase-root': {
              margin: 0,
              padding: '4px 8px 4px 16px',
            },
            '& .MuiListItemText-root': {
              textTransform: 'capitalize',
            },
          },
          panelWrapper: {
            '& .MuiDataGrid-columnsManagementHeader': {
              borderBottom: 0,
              '&. MuiInputBase-root': {
                height: '33px',
              },
            },
            '& .MuiDataGrid-columnsManagement': {
              padding: 0,
              '& .MuiFormControlLabel-root': {
                margin: 0,
                height: '48px',
                padding: '0 16px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '1rem',
                },
                '&:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                },
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: themeTokens.background.paper,
          },
        },
      },
    },
  };
};
